import Button from "../../../shared/components/UI/Button";
import Input from "../../../shared/components/UI/Input";
import Form from "../../../shared/components/UI/Form";
import Navbar from "../../../shared/components/navbar/NavBar";

import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import authService from "../services/authService";
import { AxiosError } from "axios";
import { displayNotify } from "../../../shared/services/Notifications/NotificationServices";
import "./LoginForm.scss";

interface IForm {
  username: string;
  email: string;
  password: string;
  passwordRepeat: string;
  user_callsign: string;
}

interface Props {
  isRegister: boolean;
  setIsRegister: React.Dispatch<React.SetStateAction<boolean>>;
}

const registerNotificationMap: Record<
  number,
  { title: string; description: string }
> = {
  400: {
    title: "Пользователь существует",
    description: "Логин или email уже заняты.",
  },
  403: {
    title: "Регистрация запрещена",
    description: "Регистрация отключена администрацией.",
  },
  422: {
    title: "Ошибка валидации",
    description: "Некорректные данные. Проверьте форму.",
  },
  500: {
    title: "Ошибка сервера",
    description: "Что-то пошло не так. Попробуйте позже.",
  },
};

function RegisterForm({ isRegister, setIsRegister }: Props) {
  const navigate = useNavigate();
  const { register, handleSubmit, getValues, trigger } = useForm<IForm>({
    mode: "onChange",
  });

  const regM = useMutation({
    mutationFn: (form: IForm) => {
      const { passwordRepeat, ...payload } = form;
      return authService.regFetch(payload);
    },
    onSuccess: () => {
      displayNotify({
        status: "success",
        title: "Успешно!",
        description: "Регистрация прошла успешно",
      });
      navigate("/");
    },
    onError: (error: AxiosError) => {
      if (!error.response) {
        displayNotify({
          status: "error",
          title: "Нет соединения",
          description: "Проверьте интернет или попробуйте позже.",
        });
        return;
      }

      const status = error.response.status;

      const data = error.response.data as any

      const detail = data?.detail;

      if (detail?.includes("CSRF Failed")) {
        displayNotify({
          status: "error",
          title: "Ошибка безопасности",
          description: "CSRF ошибка. Обновите страницу и попробуйте снова.",
        });
        return;
      }

      const keys = Object.keys(data)

      if (keys.length === 0) {
        const message = registerNotificationMap[status];

        displayNotify({
          status: "error",
          title: message?.title || "Неизвестная ошибка",
          description: message?.description || "Попробуйте снова позже.",
        });

        return
      }

      keys.forEach(key => {
        displayNotify({
          status: "error",
          title: key,
          description: data[key],
        });
      })


    },
  });

  const onSubmit: SubmitHandler<IForm> = async () => {
    const valid = await trigger();
    const values = getValues();

    if (!values.username) {
      displayNotify({
        status: "error",
        title: "Введите логин",
        description: "Поле логина обязательно",
      });
      return;
    }

    const minLoginLength = 3;
    const maxLoginLength = 40;

    if (
      values.username.length < minLoginLength ||
      values.username.length > maxLoginLength
    ) {
      displayNotify({
        status: "error",
        title: "Некорректный логин",
        description: `Длина логина должна быть от ${minLoginLength} до ${maxLoginLength} символов`,
      });
      return;
    }

    if (!/^[a-zA-Z0-9-_]+$/.test(values.username)) {
      displayNotify({
        status: "error",
        title: "Некорректный логин",
        description:
          "Допустимы только латиница, цифры, дефис и нижнее подчеркивание",
      });
      return;
    }

    if (!values.user_callsign) {
      displayNotify({
        status: "error",
        title: "Введите позывной",
        description: "Поле позывного обязательно",
      });
      return;
    }

    const minCallsignLength = 3;
    const maxCallsignLength = 40;

    if (
      values.user_callsign.length < minCallsignLength ||
      values.user_callsign.length > maxCallsignLength
    ) {
      displayNotify({
        status: "error",
        title: "Некорректный позывной",
        description: `Длина позывного должна быть от ${minCallsignLength} до ${maxCallsignLength} символов`,
      });
      return;
    }

    if (!values.email) {
      displayNotify({
        status: "error",
        title: "Введите email",
        description: "Поле email обязательно",
      });
      return;
    }

    if (!/^[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(values.email)) {
      displayNotify({
        status: "error",
        title: "Некорректный email",
        description: 'Email должен содержать символ "@" и быть корректным',
      });
      return;
    }

    if (!values.password) {
      displayNotify({
        status: "error",
        title: "Введите пароль",
        description: "Поле пароля обязательно",
      });
      return;
    }

    if (!/^(?=.*[A-Z])(?=.*\d).{8,}$/.test(values.password)) {
      displayNotify({
        status: "error",
        title: "Слабый пароль",
        description: "Минимум 8 символов, 1 заглавная буква и 1 цифра",
      });
      return;
    }

    if (values.password !== values.passwordRepeat) {
      displayNotify({
        status: "error",
        title: "Пароли не совпадают",
        description: "Убедитесь, что оба пароля совпадают",
      });
      return;
    }

    regM.mutate(values);
  };

  const onLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsRegister(false);
  };

  return (
    <div
      className="login-page"
      style={{ position: "relative", minHeight: "100vh", overflow: "hidden" }}
    >
      <div />
      <div
        className="login-container"
        style={{ position: "relative", zIndex: 1 }}
      >
        <Form
          noValidate
          className="login_form"
          onSubmit={handleSubmit(onSubmit)}
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid #29292A",
            borderRadius: "20px",
          }}
        >
          <div className="login_header">
            <img className="login_logo" src="/skull.svg" alt="logo" />
            <h2 className="login_title">Доброго дня, комрад!</h2>
            <p className="login_subtitle">Заполните форму регистрации</p>
          </div>

          <div className="login_fields">
            <div className="login_input-wrapper">
              <Input
                type="text"
                placeholder="Логин"
                className="login_input"
                {...register("username")}
              />
            </div>

            <div className="login_input-wrapper">
              <Input
                type="text"
                placeholder="Позывной"
                className="login_input"
                {...register("user_callsign")}
              />
            </div>

            <div className="login_input-wrapper">
              <Input
                type="email"
                placeholder="Email"
                className="login_input"
                {...register("email")}
              />
            </div>

            <div className="login_input-wrapper">
              <Input
                type="password"
                placeholder="Пароль"
                className="login_input"
                {...register("password")}
              />
            </div>

            <div className="login_input-wrapper">
              <Input
                type="password"
                placeholder="Повторите пароль"
                className="login_input"
                {...register("passwordRepeat")}
              />
            </div>
          </div>

          <div className="login_actions">
            <Button
              contraststyle="true"
              type="submit"
              disabled={regM.isPending}
              className="login_submit"
            >
              {regM.isPending ? "Регистрируемся..." : "Зарегистрироваться"}
            </Button>
            <p className="forgot-password">
              Уже есть аккаунт? - <a onClick={onLoginClick}>Войти</a>
            </p>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default RegisterForm;
